<template>
  <div class="server-overview-tab">
    <div class="server-overview-tab__uptime-row">
      <div v-for="window in uptimeWindows" :key="window.key" class="server-overview-tab__uptime-card">
        <div class="server-overview-tab__uw">Uptime — {{ window.key }}</div>
        <div class="server-overview-tab__uf">
          <span v-if="window.pct !== null">{{ window.pct.toFixed(3) }}%</span>
          <span v-else class="server-overview-tab__na">—</span>
        </div>
        <div class="server-overview-tab__uptime-bar" :aria-label="`${window.key} uptime`">
          <template v-if="window.pct !== null">
            <span class="up-ok" :style="{ flexGrow: window.pct }"></span>
            <span class="up-down" :style="{ flexGrow: Math.max(0, 100 - window.pct) }"></span>
          </template>
          <template v-else>
            <span class="up-empty"></span>
          </template>
        </div>
      </div>
    </div>

    <div class="server-overview-tab__mid-grid">
      <div class="server-overview-tab__checks-card">
        <div class="server-overview-tab__card-head">
          <h4>Service checks</h4>
          <span class="server-overview-tab__n">{{ passingLabel }}</span>
        </div>
        <div class="server-overview-tab__check-grid">
          <div v-for="svc in services.slice(0, 8)" :key="svc.unit" class="server-overview-tab__check-row">
            <span class="server-overview-tab__cdot" :class="isFailed(svc) ? 'is-down' : 'is-ok'"></span>
            <span class="server-overview-tab__cname">{{ svc.unit }}</span>
            <span class="server-overview-tab__clat">{{ svc.active_state }}</span>
          </div>
          <div v-if="!services.length" class="server-overview-tab__empty-row">No service checks reported yet.</div>
        </div>
      </div>

      <div class="server-overview-tab__spark-stack">
        <div v-for="metric in metricCards" :key="metric.key" class="server-overview-tab__spark-card">
          <div class="server-overview-tab__spark-head">
            <span class="server-overview-tab__sname">{{ metric.label }}</span>
            <span class="server-overview-tab__sval">
              <span v-if="metric.latest !== null">{{ metric.latest }}</span>
              <span v-else class="server-overview-tab__na">—</span>
            </span>
          </div>
          <ServerSparkline :series="metric.series" :empty-label="metric.emptyLabel" />
        </div>
      </div>
    </div>

    <ServerWebPressurePanel v-if="pressure" :snapshot="pressure" />

    <!-- Plan 011 §5.2 — certificate row: serial, notAfter, last rotation,
         any renewal error. No design gate needed (one row on an existing
         tab, per the plan's own note). -->
    <div class="server-overview-tab__cert-card">
      <div class="server-overview-tab__card-head">
        <h4>Agent certificate</h4>
        <span class="server-overview-tab__cert-pill" :class="'server-overview-tab__cert-pill--' + certTone">{{ certLabel }}</span>
      </div>
      <div class="server-overview-tab__cert-body">
        <div class="server-overview-tab__cert-row">
          <span class="server-overview-tab__cert-key">Expires</span>
          <span class="server-overview-tab__cert-val">
            <span v-if="server?.agent_cert_not_after">{{ formatEpoch(server.agent_cert_not_after) }}</span>
            <span v-else class="server-overview-tab__na">Unknown — agent predates cert reporting</span>
          </span>
        </div>
        <div class="server-overview-tab__cert-row">
          <span class="server-overview-tab__cert-key">Serial</span>
          <span class="server-overview-tab__cert-val">{{ server?.agent_cert_serial || '—' }}</span>
        </div>
        <div class="server-overview-tab__cert-row">
          <span class="server-overview-tab__cert-key">Last reported</span>
          <span class="server-overview-tab__cert-val">
            <span v-if="server?.agent_cert_reported_at">{{ formatRelativeTime(server.agent_cert_reported_at) }}</span>
            <span v-else class="server-overview-tab__na">—</span>
          </span>
        </div>
        <div v-if="server?.agent_cert_renewal_error" class="server-overview-tab__cert-row server-overview-tab__cert-row--error">
          <span class="server-overview-tab__cert-key">Renewal error</span>
          <span class="server-overview-tab__cert-val">{{ server.agent_cert_renewal_error }}<template v-if="server.agent_cert_renewal_failed_at"> ({{ formatRelativeTime(server.agent_cert_renewal_failed_at) }})</template></span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import useApi from '@apexl/icarus-core/src/services/api';
import ServerSparkline from '@root/components/Servers/ServerSparkline.vue';
import ServerWebPressurePanel from '@root/components/Servers/ServerWebPressurePanel.vue';
import { certExpiryLabel, certExpiryTone, formatEpoch, formatRelativeTime } from '@root/services/serverFormat';

const props = defineProps({
  apiBase: { type: String, required: true },
  serverId: { type: [Number, String], required: true },
  server: { type: Object, default: null },
});

const certTone = computed(() => certExpiryTone(props.server));
const certLabel = computed(() => certExpiryLabel(props.server));

const api = useApi();

const uptime = ref({ '24h': null, '30d': null, '90d': null });
const services = ref([]);
const metrics = ref({ load: [], disk_pct: [], mem_pct: [] });
// Latest host resource-pressure snapshot (per-mount disk, memory, top CPU
// consumers, PHP-FPM pool saturation, per-vhost request rates). Null until
// the agent reports it; the panel is hidden entirely rather than rendering a
// grid of empty cards on a server whose agent predates that telemetry.
const pressure = ref(null);

// Display-only defence-in-depth: the API/rollup layer is the actual fix for
// the >100% uptime bug (union-of-overlapping-intervals in
// UptimeRollupService, self-healing stray connectivity rows in
// ObservabilityIngestService), but this tile never renders an impossible
// percentage regardless of what a stale cached response contains.
const clampPct = (value) => (value === null || value === undefined ? null : Math.min(100, Math.max(0, value)));

const uptimeWindows = computed(() => (['24h', '30d', '90d'].map((key) => ({
  key,
  pct: clampPct(uptime.value[key]?.uptime_pct ?? null),
}))));

const passingLabel = computed(() => {
  if (!services.value.length) {
    return '';
  }
  const passing = services.value.filter((s) => !isFailed(s)).length;
  return `${passing} of ${services.value.length} passing`;
});

function isFailed(svc) {
  return svc.active_state === 'failed';
}

// Labelled "Load" (not "CPU") deliberately — `vitals_load`/the `load` metric
// series is a real Unix load average (luna-node's `loadAverage[0]`), not a
// CPU percentage; relabelling it "CPU %" would misrepresent the unit even
// though the design mockup's card says "CPU". The card's sub-label carries
// the per-core figure when the agent reports it, which is the number that is
// actually comparable across boxes (and the one `host.cpu_saturation`
// thresholds on).
//
// Memory and Disk were empty placeholders until luna-node started shipping
// `memPct`/`diskPct` on the heartbeat (2026-08-01). Their empty states now
// mean "this agent is too old" rather than "the platform can't do this", and
// are worded accordingly — still an honest empty state, never a fabricated
// series.
const metricCards = computed(() => ([
  {
    key: 'load',
    label: loadLabel.value,
    series: metrics.value.load,
    latest: metrics.value.load.length ? Number(metrics.value.load[metrics.value.load.length - 1].value).toFixed(2) : null,
    emptyLabel: 'No load samples yet',
  },
  {
    key: 'mem_pct',
    label: 'Memory',
    series: metrics.value.mem_pct,
    latest: metrics.value.mem_pct.length ? `${Math.round(metrics.value.mem_pct[metrics.value.mem_pct.length - 1].value)}%` : null,
    emptyLabel: 'No memory samples yet (agent update required)',
  },
  {
    key: 'disk_pct',
    label: 'Disk',
    series: metrics.value.disk_pct,
    latest: metrics.value.disk_pct.length ? `${Math.round(metrics.value.disk_pct[metrics.value.disk_pct.length - 1].value)}%` : null,
    emptyLabel: 'No disk samples yet (agent update required)',
  },
]));

// e.g. "Load (4 cores)" — the core count is what makes a load figure
// interpretable at all, so show it as soon as the agent reports one.
const loadLabel = computed(() => {
  const cores = pressure.value?.cores;
  return cores ? `Load (${cores} core${cores === 1 ? '' : 's'})` : 'Load';
});

async function load() {
  try {
    const [uptimeData, servicesData, metricsData, pressureData] = await Promise.all([
      api.get(`${props.apiBase}/${props.serverId}/uptime`),
      api.get(`${props.apiBase}/${props.serverId}/services`),
      api.get(`${props.apiBase}/${props.serverId}/metrics`, { params: { window: '24h' } }),
      api.get(`${props.apiBase}/${props.serverId}/pressure`),
    ]);
    uptime.value = uptimeData.windows || {};
    services.value = servicesData.services || [];
    metrics.value = metricsData.metrics || { load: [], disk_pct: [], mem_pct: [] };
    pressure.value = pressureData.snapshot || null;
  } catch {
    // Left at defaults — the tab renders its own empty states either way.
  }
}

onMounted(load);
</script>

<style lang="scss">
.server-overview-tab {
  &__na {
    color: var(--theme-text-tertiary);
  }

  &__uptime-row {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px;
    margin-bottom: 16px;

    @media (max-width: 700px) {
      grid-template-columns: 1fr;
    }
  }

  &__uptime-card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    padding: 14px 16px;
  }

  &__uw {
    font-size: 10.5px;
    font-weight: 700;
    letter-spacing: .05em;
    text-transform: uppercase;
    color: var(--theme-text-tertiary);
    margin-bottom: 6px;
  }

  &__uf {
    font-size: 19px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    margin-bottom: 8px;
  }

  &__uptime-bar {
    display: flex;
    gap: 1.5px;
    height: 16px;

    .up-ok { background: var(--theme-status-success); border-radius: 2px; }
    .up-down { background: var(--theme-status-danger); border-radius: 2px; }
    .up-empty {
      flex-grow: 1;
      border-radius: 2px;
      background: repeating-linear-gradient(45deg, var(--theme-border-soft), var(--theme-border-soft) 4px, var(--theme-bg-content) 4px, var(--theme-bg-content) 8px);
    }
  }

  &__mid-grid {
    display: grid;
    grid-template-columns: 1.15fr 1fr;
    gap: 16px;
    align-items: start;

    @media (max-width: 900px) {
      grid-template-columns: 1fr;
    }
  }

  &__checks-card, &__spark-card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
  }

  &__card-head {
    padding: 14px 18px 10px;
    border-bottom: 1px solid var(--theme-border-soft);
    display: flex;
    justify-content: space-between;
    align-items: center;

    h4 {
      margin: 0;
      font-size: 13.5px;
      font-weight: 600;
    }
  }

  &__n {
    font-size: 11px;
    color: var(--theme-text-tertiary);
  }

  &__check-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;

    @media (max-width: 500px) {
      grid-template-columns: 1fr;
    }
  }

  &__check-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-top: 1px solid var(--theme-border-soft);
    font-size: 12px;
  }

  &__cdot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    flex-shrink: 0;

    &.is-ok { background: var(--theme-status-success); }
    &.is-down { background: var(--theme-status-danger); }
  }

  &__cname {
    flex: 1;
    font-weight: 500;
  }

  &__clat {
    font-family: var(--theme-font-mono);
    color: var(--theme-text-tertiary);
    font-size: 10.5px;
  }

  &__empty-row {
    padding: 16px 18px;
    color: var(--theme-text-tertiary);
    font-size: 12px;
  }

  &__spark-stack {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__spark-card {
    padding: 13px 16px 10px;
  }

  &__spark-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 4px;
  }

  &__sname {
    font-size: 12px;
    font-weight: 600;
    color: var(--theme-text-secondary);
  }

  &__sval {
    font-size: 16px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
  }

  &__cert-card {
    background: var(--theme-bg-card);
    border: 1px solid var(--theme-border);
    border-radius: var(--theme-radius-lg, 10px);
    box-shadow: var(--theme-shadow-card);
    margin-top: 16px;
  }

  &__cert-body {
    padding: 4px 18px 12px;
  }

  &__cert-row {
    display: flex;
    justify-content: space-between;
    gap: 12px;
    padding: 8px 0;
    border-top: 1px solid var(--theme-border-soft);
    font-size: 12.5px;

    &:first-child {
      border-top: none;
    }

    &--error {
      color: var(--theme-status-danger, #a53232);
    }
  }

  &__cert-key {
    color: var(--theme-text-tertiary);
    font-weight: 600;
  }

  &__cert-val {
    text-align: right;
    font-family: var(--theme-font-mono);
  }

  &__cert-pill {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .02em;
    padding: 2.5px 8px;
    border-radius: 3px;
    white-space: nowrap;

    &--cert-ok {
      background: var(--theme-border-soft);
      color: var(--theme-text-secondary);
    }

    &--cert-warn {
      background: #fbeecd;
      color: #8a6116;
    }

    &--cert-crit {
      background: #f8dede;
      color: #a53232;
    }

    &--cert-unknown {
      background: var(--theme-border-soft);
      color: var(--theme-text-tertiary);
    }
  }
}
</style>
